import { useState } from 'react'
import type { TemplateJson } from '../../types/template'
import { detectGeoFields } from '../../utils/geo/formats'
import type { GeoJsonGeometry } from '../../types/geo'
import { useToast } from '../../hooks/useToast'

interface Props {
  template: TemplateJson & { _values: Record<string, unknown> }
  drawnGeometry: GeoJsonGeometry | null
  onBack: () => void
  onClose: () => void
}

/**
 * Builds the final payload shape used for submission.
 *
 * Integration contract:
 * - `fields[].value` contains user-resolved values for every template field.
 * - `_geometry.primary` is the geometry drawn on the map.
 * - `_geometry.all_geo_fields` mirrors all geo-capable fields for downstream systems.
 * - Internal `_values` is removed before payload leaves the client.
 */
function buildCollectionRequestPayload(
  template: TemplateJson & { _values: Record<string, unknown> },
  drawnGeometry: GeoJsonGeometry | null
) {
  const geoFieldIds = detectGeoFields(template.fields)
  const allGeoFields = geoFieldIds.map((id) => {
    const field = template.fields.find((f) => f.id === id)
    return { field_id: id, label: field?.label ?? id, value: template._values[id] }
  })

  const filledFields = template.fields.map((f) => ({
    ...f,
    value: template._values[f.id] ?? f.value,
  }))

  const output = {
    ...template,
    fields: filledFields,
    _geometry: {
      primary: drawnGeometry,
      all_geo_fields: allGeoFields,
    },
  }

  // `_values` is UI-only transient state and should never be sent to an API.
  const { _values, ...cleanOutput } = output as typeof output & { _values: unknown }
  return cleanOutput
}

export default function ReviewSubmit({ template, drawnGeometry, onBack, onClose }: Props) {
  const [submitted, setSubmitted] = useState(false)
  const { toast } = useToast()

  // Single payload assembly path used by copy/download/submit.
  const cleanOutput = buildCollectionRequestPayload(template, drawnGeometry)

  const jsonStr = JSON.stringify(cleanOutput, null, 2)

  function handleCopy() {
    navigator.clipboard.writeText(jsonStr)
    toast({ title: 'Collection request copied', description: 'JSON copied to clipboard' })
  }

  function handleDownload() {
    const blob = new Blob([jsonStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${template.meta.name.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast({ title: 'Downloaded' })
  }

  function handleSubmit() {
    // TODO(api-integration): Replace this prototype with a real API mutation.
    // Expected flow:
    // 1) POST `cleanOutput` to your backend endpoint.
    // 2) Set submitted=true only on HTTP success.
    // 3) Show backend error messages in the destructive toast path.
    //
    // Example:
    // await api.post('/collection-requests', cleanOutput)
    // setSubmitted(true)
    console.log('[CHEESY] Collection request:', cleanOutput)
    setSubmitted(true)
    toast({
      title: 'Collection request submitted',
      description: 'Request logged to console (prototype mode)',
    })
  }

  if (submitted) {
    return (
      <div className="space-y-6 text-center py-8">
        <div className="text-4xl">✓</div>
        <div>
          <h3 className="text-lg font-semibold">Request Submitted</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Your collection request has been submitted (prototype mode — see browser console).
          </p>
        </div>
        <button
          onClick={onClose}
          className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground"
        >
          Close
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold">Review Collection Request</h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          Verify the assembled JSON before submitting.
        </p>
      </div>

      <div className="relative">
        <pre className="w-full max-h-80 overflow-auto rounded-md bg-muted/50 p-3 text-xs font-mono border border-border leading-relaxed">
          {/* Simple syntax highlighting via CSS spans */}
          <GeometryHighlightedJson json={jsonStr} />
        </pre>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={handleCopy}
          className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent/50"
        >
          Copy JSON
        </button>
        <button
          onClick={handleDownload}
          className="rounded-md border border-border px-3 py-1.5 text-xs hover:bg-accent/50"
        >
          Download
        </button>
      </div>

      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onBack}
          className="flex-1 rounded-md border border-border px-4 py-2 text-sm hover:bg-accent/50"
        >
          ← Back
        </button>
        <button
          onClick={handleSubmit}
          className="flex-1 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700"
        >
          Submit Request
        </button>
      </div>
    </div>
  )
}

/** Minimal syntax highlighter that marks _geometry key */
function GeometryHighlightedJson({ json }: { json: string }) {
  const lines = json.split('\n')
  return (
    <>
      {lines.map((line, i) => {
        const isGeoKey = line.includes('"_geometry"')
        return (
          <span
            key={i}
            className={isGeoKey ? 'text-yellow-400' : 'text-foreground/80'}
          >
            {line + '\n'}
          </span>
        )
      })}
    </>
  )
}
